import { useEffect, useMemo, useState } from 'react'

import { Box, CollapsableCard, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Counter } from '@factorialco/f0-react/dist/experimental'
import clouds from 'assets/images/import-clouds.svg'
import {
  BulkCreateCompensationsFileDocument,
  CompensationsValueObjectsFileCategoryTypeEnum,
  PayrollRunFileCategoryFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/xml': '.xml',
  'text/xml': '.xml',
  'text/csv': '.csv',
  'image/jpeg': '.jpg',
  'image/png': '.png',
}

const ACCEPTED_CONTENT_TYPES_BY_CATEGORY: Partial<
  Record<CompensationsValueObjectsFileCategoryTypeEnum, string[]>
> = {
  payroll_result: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ],
  payslips: ['application/pdf'],
  payment_method: ['application/xml', 'text/xml'],
  others: [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'image/jpeg',
    'image/png',
  ],
}

const getAcceptedExtensions = (
  categoryType: CompensationsValueObjectsFileCategoryTypeEnum
): string => {
  const mimeTypes = ACCEPTED_CONTENT_TYPES_BY_CATEGORY[categoryType] || []
  return [
    ...new Set(mimeTypes.map((mimeType) => MIME_TYPE_TO_EXTENSION[mimeType] || mimeType)),
  ].join(', ')
}

interface DocumentSection {
  id: CompensationsValueObjectsFileCategoryTypeEnum
  label: string
  files: File[]
  categoryId: string
}

interface DocumentSectionWithFiles {
  id: string
  files: File[]
  categoryId?: string
}

interface FileSidebarProps {
  open: boolean
  onClose: () => void
  onAddDocument?: (files: File[], sectionId: string, categoryId?: string) => void
  onRemoveDocument?: (fileIndex: number, sectionId: string) => void
  onSuccess: () => void
  title?: string
  payrollRunId: string
  fileCategories: PayrollRunFileCategoryFragment[]
}

const getCategoryLabel = (categoryType: CompensationsValueObjectsFileCategoryTypeEnum): string => {
  const labels: Partial<Record<CompensationsValueObjectsFileCategoryTypeEnum, string>> = {
    payroll_result: i18n.t(
      'bookkeepers.clients.payroll_cycles.documents.file_categories.payroll_result'
    ),
    payslips: i18n.t('bookkeepers.clients.payroll_cycles.documents.file_categories.payslips'),
    payment_method: i18n.t(
      'bookkeepers.clients.payroll_cycles.documents.file_categories.payment_method'
    ),
    others: i18n.t('bookkeepers.clients.payroll_cycles.documents.file_categories.others'),
  }
  return labels[categoryType] || categoryType
}

const FileSidebar = ({
  open,
  onClose,
  onAddDocument,
  onRemoveDocument,
  onSuccess,
  title = i18n.t('bookkeepers.clients.payroll_cycles.documents.add_document'),
  payrollRunId,
  fileCategories,
}: FileSidebarProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openSection, setOpenSection] =
    useState<CompensationsValueObjectsFileCategoryTypeEnum | null>('payroll_result')
  const [sectionFiles, setSectionFiles] = useState<
    Partial<Record<CompensationsValueObjectsFileCategoryTypeEnum, File[]>>
  >({})
  const { company: currentCompany } = useCurrentSession()
  const companyId = currentCompany.id

  const [bulkCreateFiles, { loading: bulkCreateFilesLoading }] = useMutation(
    BulkCreateCompensationsFileDocument,
    {
      extractorFn: (result) => result.compensationsMutations.bulkCreateCompensationsFile,
    }
  )

  useEffect(() => {
    setIsLoading(bulkCreateFilesLoading)
  }, [bulkCreateFilesLoading])

  const sections: DocumentSection[] = useMemo(() => {
    return fileCategories.map((category) => ({
      id: category.categoryType,
      label: getCategoryLabel(category.categoryType),
      files: [],
      categoryId: category.id,
    }))
  }, [fileCategories])

  const toggleSection = (sectionId: CompensationsValueObjectsFileCategoryTypeEnum) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId))
  }

  const handleFileDrop = (
    files: File[],
    sectionId: CompensationsValueObjectsFileCategoryTypeEnum
  ) => {
    const section = sections.find((s) => s.id === sectionId)
    const updatedFiles = [...(sectionFiles[sectionId] || []), ...files]
    setSectionFiles((prev) => ({
      ...prev,
      [sectionId]: updatedFiles,
    }))

    onAddDocument?.(files, sectionId, section?.categoryId)
  }

  const handleRemoveFile = (
    fileIndex: number,
    sectionId: CompensationsValueObjectsFileCategoryTypeEnum
  ) => {
    const updatedFiles = (sectionFiles[sectionId] || []).filter((_, index) => index !== fileIndex)
    setSectionFiles((prev) => ({
      ...prev,
      [sectionId]: updatedFiles,
    }))
    onRemoveDocument?.(fileIndex, sectionId)
  }

  const getSectionFiles = (sectionId: CompensationsValueObjectsFileCategoryTypeEnum): File[] => {
    return sectionFiles[sectionId] || []
  }

  const getSectionCount = (sectionId: CompensationsValueObjectsFileCategoryTypeEnum): number => {
    return getSectionFiles(sectionId).length
  }

  const handleCancel = () => {
    setSectionFiles({})
    setOpenSection('payroll_result')
    onClose()
  }

  const handleAddDocument = async () => {
    const sectionsWithFiles: DocumentSection[] = sections.map((section) => ({
      ...section,
      files: sectionFiles[section.id] || [],
    }))
    trackUploadedDocuments(sectionsWithFiles)
    await onSubmit(sectionsWithFiles)
  }

  const trackUploadedDocuments = (sectionsWithFiles: DocumentSection[]) => {
    const initialNumberOfFiles = 0
    const numberOfFiles = sectionsWithFiles.reduce(
      (accumulator, section) => accumulator + section.files.length,
      initialNumberOfFiles
    )
    const importedFileTypes = sectionsWithFiles.map((section) => section.id)
    track(trackingEvents.bookkeeper.cycles.documentsImportedInProfessionals, {
      company_id: companyId,
      number_of_files_imported: numberOfFiles,
      imported_file_types: importedFileTypes,
    })
  }

  const onSubmit = async (sectionsToSubmit: DocumentSectionWithFiles[]) => {
    const files = sectionsToSubmit
      .filter(
        (section): section is { id: string; files: File[]; categoryId: string } =>
          section.files.length > 0 && section.categoryId !== undefined
      )
      .flatMap((section) =>
        section.files.map((file) => ({
          fileCategoryId: section.categoryId,
          file,
        }))
      )

    if (files.length === 0) return

    setIsLoading(true)

    const response = await bulkCreateFiles({
      context: { isBookkeeperView: true },
      variables: {
        companyId,
        payrollRunId,
        files,
      },
    })

    setIsLoading(false)

    const result = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('bookkeepers.clients.payroll_cycles.documents.upload_error'),
      })
    })

    if (!result.success) {
      return
    }

    app.setFlash({
      type: 'correct',
      message: i18n.t('bookkeepers.clients.payroll_cycles.documents.upload_success'),
    })

    setSectionFiles({})
    setOpenSection('payroll_result')
    onSuccess()
    onClose()
  }

  return (
    <F0Dialog
      isOpen={open}
      onClose={onClose}
      position="right"
      title={title}
      primaryAction={{
        label: i18n.t('bookkeepers.clients.payroll_cycles.documents.add_document'),
        onClick: handleAddDocument,
        disabled: sections.every((section) => getSectionCount(section.id) === 0),
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleCancel,
      }}
    >
      {isLoading ? (
        <Box width="full" alignItems="center" paddingY="s32">
          <Spinner />
        </Box>
      ) : (
        <Box gap="s16" padding="s16">
          {sections.map((section) => {
            const isOpen = openSection === section.id
            const files = getSectionFiles(section.id)
            const count = getSectionCount(section.id)

            return (
              <CollapsableCard
                key={section.id}
                open={isOpen}
                contentPadding="s0"
                contentBackground="grey000White"
                Card={
                  <Box
                    flexDirection="row"
                    justifyContent="spaceBetween"
                    alignItems="center"
                    onClick={() => toggleSection(section.id)}
                    style={{ cursor: 'pointer' }}
                    padding="s4"
                  >
                    <Box flexDirection="row" gap="s8" alignItems="center">
                      <Text.Normal size="300" weight="semibold" color="textPrimary">
                        {section.label}
                      </Text.Normal>
                      <Counter value={count} type={count > 0 ? 'selected' : undefined} />
                    </Box>
                  </Box>
                }
                Content={
                  <Box
                    background="grey100"
                    padding="s16"
                    borderRadius={{ bottomLeft: 'abs008', bottomRight: 'abs008' }}
                  >
                    <Box background="white" borderRadius={{ all: 'abs008' }}>
                      <DropzoneBox
                        onDrop={(droppedFiles) => handleFileDrop(droppedFiles, section.id)}
                        icon={clouds}
                        message={i18n.t('documents.drop_here')}
                        description={i18n.t(
                          'bookkeepers.clients.payroll_cycles.documents.upload_description',
                          { extensions: getAcceptedExtensions(section.id) }
                        )}
                        accept={acceptedContentTypes(
                          ACCEPTED_CONTENT_TYPES_BY_CATEGORY[section.id]
                        )}
                      />
                    </Box>
                    <Box paddingTop="s16">
                      {files.length > 0 && (
                        <FileList
                          spaced={false}
                          files={files}
                          onDeleteFile={(index) => handleRemoveFile(index, section.id)}
                          background="white"
                        />
                      )}
                    </Box>
                  </Box>
                }
              />
            )
          })}
        </Box>
      )}
    </F0Dialog>
  )
}

export default FileSidebar
